import React, { useEffect, useState } from "react";
import { User, Mail, Phone, MapPin, Target, FileText, IdCard, Calendar, Save, Lock, Camera, History as HistoryIcon, Download, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS, ROLE_COLORS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

export default function ProfilePage() {
  const { user, setUser, customRole } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [loas, setLoas] = useState([]);

  useEffect(() => {
    (async () => {
      const r = await api.get("/users/me/profile");
      setProfile(r.data);
      setForm({
        name: r.data.name || "",
        contact_number: r.data.contact_number || "",
        address: r.data.address || "",
        goals: r.data.goals || "",
        bio: r.data.bio || "",
      });
      try {
        const l = await api.get("/loas", { params: { for_user: r.data.user_id } });
        setLoas(l.data || []);
      } catch {}
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/users/me/profile", form);
      setProfile(r.data);
      // Refresh global user
      setUser?.({ ...user, name: r.data.name });
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadPic = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.patch("/users/me/profile", { picture: reader.result });
        const r = await api.get("/users/me/profile");
        setProfile(r.data);
        setUser?.({ ...user, picture: r.data.picture });
        toast.success("Photo updated");
      } catch (e) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  const downloadLoa = (lid) => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/loas/${lid}/pdf?t=${token}`, "_blank");
  };

  if (!profile) return <div className="p-12 text-slate-400">Loading…</div>;

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="profile-page">
        <PageHeader
          title="My Profile"
          subtitle="Personal information you control. Locked fields are managed by Admin."
          icon={User}
          accent="indigo"
          actions={<button onClick={save} disabled={busy} className="btn-primary" data-testid="profile-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>}
        />

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Identity card */}
          <div className="dashboard-card p-6 lg:col-span-1">
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                <div className="w-28 h-28 rounded-3xl bg-gradient-to-br from-indigo-400 to-fuchsia-400 flex items-center justify-center text-white font-display font-black text-4xl overflow-hidden" data-testid="profile-avatar">
                  {profile.picture ? <img src={profile.picture} alt="" className="w-full h-full object-cover" /> : profile.name?.charAt(0).toUpperCase()}
                </div>
                <label className="absolute -bottom-1 -right-1 p-2 bg-white rounded-full shadow-md cursor-pointer hover:bg-indigo-50">
                  <Camera className="w-4 h-4 text-indigo-600" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPic(e.target.files?.[0])} data-testid="profile-photo-upload" />
                </label>
              </div>
              <div className="font-display font-black text-xl mt-3">{profile.name}</div>
              <div className={`role-pill mt-1 ${ROLE_COLORS[profile.role] || "bg-slate-100"}`} data-testid="profile-role-badge">{ROLE_LABELS[profile.role] || profile.role}</div>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <LockedRow label="Email" value={profile.email} Icon={Mail} />
              <LockedRow label="Member ID" value={profile.member_id || "—"} Icon={IdCard} testid="profile-member-id" />
              <LockedRow label="City / Region" value={profile.city || profile.region || "—"} Icon={MapPin} />
              <LockedRow label="Designation" value={ROLE_LABELS[profile.role] || profile.role} Icon={Lock} hint="Admin-managed" />
              <LockedRow label="End of Tenure" value={profile.end_tenure_date || "—"} Icon={Calendar} testid="profile-end-tenure" hint="Auto-updated when LOA renewed" />
            </div>
          </div>

          {/* Editable fields */}
          <div className="lg:col-span-2 space-y-5">
            <div className="dashboard-card p-6 space-y-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><User className="w-5 h-5 text-indigo-500" /> Personal Information</h3>
              <Row label="Display Name" Icon={User}>
                <input className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
              </Row>
              <Row label="Contact Number" Icon={Phone}>
                <input className="input-pill" placeholder="+91 90000-00000" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} data-testid="profile-contact" />
              </Row>
              <Row label="Address" Icon={MapPin}>
                <textarea rows={2} className="input-pill" placeholder="Street, city, country" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" />
              </Row>
            </div>

            <div className="dashboard-card p-6 space-y-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><Target className="w-5 h-5 text-fuchsia-500" /> Goals & Bio</h3>
              <Row label="Your goals on the platform" Icon={Target}>
                <textarea rows={3} className="input-pill" placeholder="e.g. Run 4 MUNs this year…" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} data-testid="profile-goals" />
              </Row>
              <Row label="Short bio" Icon={FileText}>
                <textarea rows={3} className="input-pill" placeholder="Tell your team about yourself" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio" />
              </Row>
            </div>

            {/* LOA history */}
            {loas.filter((l) => l.issued_to === profile.user_id).length > 0 && (
              <div className="dashboard-card p-6">
                <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><HistoryIcon className="w-5 h-5 text-emerald-500" /> My Letters of Appointment</h3>
                <div className="space-y-2">
                  {loas.filter((l) => l.issued_to === profile.user_id).map((l) => (
                    <div key={l.loa_id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border-l-4 border-emerald-400" data-testid={`my-loa-${l.loa_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{l.title}</div>
                        <div className="text-xs text-slate-500">
                          From <b>{l.issued_by_name}</b> · {l.tenure_start || "—"} → <b className="text-emerald-700">{l.tenure_end}</b>
                          {l.last_renewed_at && <span className="ml-2 text-amber-700"><RefreshCcw className="w-3 h-3 inline" /> renewed</span>}
                        </div>
                      </div>
                      <button onClick={() => downloadLoa(l.loa_id)} className="btn-ghost text-xs py-1.5 px-3" data-testid={`my-loa-pdf-${l.loa_id}`}><Download className="w-3 h-3" /> PDF</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, Icon, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      {children}
    </div>
  );
}

function LockedRow({ label, value, Icon, hint, testid }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wide">{label}</div>
        <div className="font-semibold text-slate-800 truncate" data-testid={testid}>{value}</div>
        {hint && <div className="text-[10px] text-slate-400 italic">{hint}</div>}
      </div>
    </div>
  );
}
